import {
  createElement,
  forwardRef,
  Fragment,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { postfixTestId } from '../../../util/testId';
import { headingId } from '../ObjectSwitcherPanel/';
import { ObjectSwitcherItem, ObjectSwitcherProps, ObjectSwitcherSection } from '../types';
import { FloatingPanel } from '../FloatingPanel/FloatingPanel';
import { setRef } from '../../../util/ref-util';
import { useMounted } from '../../../hooks/useMounted';
import { ObjectSwitcherLaunchButton } from '../ObjectSwitcherLaunchButton';
import { ObjectSwitcherPanel } from '../ObjectSwitcherPanel/ObjectSwitcherPanel';

/**
 * The "Opened Items Dialog".
 * A Button that displays a FloatingPanel, which contains open objects
 * @see https://rndconfluence.sas.com/confluence/pages/viewpage.action?spaceKey=NOVA&title=ObjectManager
 */
export const ObjectSwitcher = forwardRef<HTMLButtonElement, ObjectSwitcherProps>(
  function ObjectSwitcher(
    { testId, isOpen, disabled, onPress, onDismiss, sections, panelProps, focusable },
    forwardedRef
  ) {
    const panelRef = useRef<HTMLDivElement>(null);
    const firstItemRef = useRef<HTMLLIElement>(null);
    const launchButtonRef = useRef<HTMLButtonElement>(null);

    const [showMenuFocus, setShowMenuFocus] = useState(false);
    const [floatingPanelTabIndex, setFloatingPanelTabIndex] = useState(0);

    const mounted = useMounted();

    const onOpenCompleteHandler = useCallback(() => {
      // There is an a11y problem with our scenario of having a menu inside
      // of a dialog. When focus is initially set within the menu, the menu
      // effectively stops propagation of the JAWS cursor up to the role=dialog
      // element, so the dialog doesn't get read.
      // We get around that by explicitly putting focus on the dialog,
      // with a delay so that JAWS can catch up, and then finally putting focus
      // on the first list item so the Menu can be read
      // Along the way, need to swap focusability/tabability between the two elements
      const dialog = panelRef.current?.parentElement;
      dialog?.focus();
      setTimeout(() => {
        if (!mounted.current) return;
        setFloatingPanelTabIndex(-1);
        setShowMenuFocus(true);
        firstItemRef.current?.focus();
      }, 500);
    }, [mounted]);

    const onCloseCompleteHandler = useCallback(
      ({ closingReason }: { closingReason: any }) => {
        onDismiss();
        if (closingReason === 'escape') {
          setTimeout(() => {
            if (!mounted.current) return;
            launchButtonRef.current?.focus();
          }, 0);
        }
      },
      [onDismiss, mounted]
    );

    const onObjectCloseAllHandler = useCallback(() => {
      panelProps.onObjectCloseAll?.({
        isDirty: hasDirty(sections),
      });
    }, [panelProps, sections]);

    // When floating panel is opened:
    // - disable menus ability to focus
    // - make the role=dialog element focusable
    // This is in preparation for a JAWS hack described
    // in the floatingPanel onOpenAnimationComplete callback below
    useEffect(() => {
      if (isOpen) {
        setShowMenuFocus(false);
        setFloatingPanelTabIndex(0);
      }
    }, [isOpen]);

    return (
      <Fragment>
        <ObjectSwitcherLaunchButton
          testId={postfixTestId(testId, 'launchButton')}
          ref={(element) => {
            setRef(forwardedRef, element);
            setRef(launchButtonRef, element);
          }}
          focusable={focusable}
          disabled={disabled}
          numberOfObjects={determineNumberOfObjects(sections)}
          onPress={onPress}
        />
        {isOpen ? (
          <FloatingPanel
            isOpen={isOpen}
            testId={postfixTestId(testId, 'floatingPanel')}
            role="dialog"
            tabIndex={floatingPanelTabIndex}
            ariaLabelledBy={headingId}
            onCloseComplete={onCloseCompleteHandler}
            onOpenComplete={onOpenCompleteHandler}
          >
            <ObjectSwitcherPanel
              testId={postfixTestId(testId, 'objectSwitcherPanel')}
              ref={panelRef}
              showFocus={showMenuFocus}
              firstItemRef={firstItemRef}
              sections={sections}
              onObjectChange={panelProps.onObjectChange}
              onObjectClose={panelProps.onObjectClose}
              onObjectCloseAll={onObjectCloseAllHandler}
            />
          </FloatingPanel>
        ) : null}
      </Fragment>
    );
  }
);

export const determineNumberOfObjects = (sections: ObjectSwitcherSection[]) => {
  const howMany = (objects: readonly ObjectSwitcherItem[]): number =>
    objects.reduce((acc, { items }) => acc + howMany(items || []), objects.length);
  return sections.reduce((acc, { items }) => acc + howMany(items), 0);
};

export const hasDirty = (sections: ObjectSwitcherSection[]) => {
  const hasDirtyItem = (objects: readonly ObjectSwitcherItem[]): boolean =>
    objects.some(({ isDirty, items }) => (isDirty ? true : items && hasDirtyItem(items)));
  return sections.some(({ items }) => hasDirtyItem(items));
};
